/**
 * WebSocket Context for managing real-time connections across the app
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { WebSocketClient, WebSocketMessage, MessageHandler } from '../services/websocket';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
  /** Whether WebSocket is currently connected */
  isConnected: boolean;
  /** Subscribe to WebSocket messages */
  subscribe: (handler: MessageHandler) => () => void;
  /** Send a message through WebSocket */
  send: (data: unknown) => void;
  /** Manually reconnect */
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const clientRef = useRef<WebSocketClient | null>(null);
  const pendingHandlersRef = useRef<Map<MessageHandler, () => void>>(new Map());

  // Initialize WebSocket client when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up existing connection if user logs out
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
        setIsConnected(false);
      }
      pendingHandlersRef.current.clear();
      return;
    }

    // Create new WebSocket client
    // Note: token parameter is vestigial - WebSocket auth uses session token from API
    // With cookie-based auth (Issue #289), session API call includes auth cookie automatically
    const client = new WebSocketClient({
      token: '', // Not used - kept for backwards compatibility
      onOpen: () => {
        setIsConnected(true);

        // Attach all pending handlers and store their cleanup functions
        if (pendingHandlersRef.current.size > 0) {
          const handlersToAttach = Array.from(pendingHandlersRef.current.keys());

          handlersToAttach.forEach(handler => {
            const cleanup = client.addMessageHandler(handler);
            // Update the map with the actual cleanup function
            pendingHandlersRef.current.set(handler, cleanup);
          });
        }
      },
      onClose: () => {
        setIsConnected(false);
      },
      onError: (error) => {
        console.error('[WebSocketContext] Error:', error);
      },
      autoReconnect: true,
      reconnectDelay: 5000,
    });

    // Connect asynchronously
    client.connect().catch(error => {
      console.error('[WebSocketContext] Failed to connect:', error);
    });
    clientRef.current = client;

    // Cleanup on unmount or auth change
    return () => {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // Subscribe to messages
  const subscribe = useCallback((handler: MessageHandler) => {
    if (clientRef.current && clientRef.current.isConnected) {
      // Client is ready, add handler immediately
      return clientRef.current.addMessageHandler(handler);
    } else {
      // Client not ready yet, queue the handler with a placeholder cleanup
      // Store with a no-op cleanup initially (will be replaced when client connects)
      const noopCleanup = () => {};
      pendingHandlersRef.current.set(handler, noopCleanup);

      // Return cleanup function
      return () => {
        const cleanup = pendingHandlersRef.current.get(handler);
        if (cleanup) {
          cleanup(); // Call the actual cleanup (either no-op or real unsubscribe)
          pendingHandlersRef.current.delete(handler);
        }
      };
    }
  }, []);

  // Send message
  const send = useCallback((data: unknown) => {
    if (clientRef.current) {
      clientRef.current.send(data);
    } else {
      console.warn('[WebSocketContext] Cannot send - not connected');
    }
  }, []);

  // Manual reconnect
  const reconnect = useCallback(() => {
    if (clientRef.current && isAuthenticated) {
      clientRef.current.disconnect();
      clientRef.current.connect().catch(error => {
        console.error('[WebSocketContext] Reconnect failed:', error);
      });
    }
  }, [isAuthenticated]);

  const value: WebSocketContextType = {
    isConnected,
    subscribe,
    send,
    reconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to subscribe to specific WebSocket message types
 */
export const useWebSocketMessage = (
  messageType: string,
  handler: (data: WebSocketMessage) => void,
  deps: React.DependencyList = []
) => {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const messageHandler = (message: WebSocketMessage) => {
      if (message.type === messageType) {
        handler(message);
      }
    };

    const unsubscribe = subscribe(messageHandler);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageType, subscribe, ...deps]);
};
